import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { getApi } from "../../../api/api";
import {
  DeliveryService,
  DeliveryType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
} from "../../../common/types/enums";
import {
  CartItem,
  OrderInterface,
  PromoInterface,
  SaleItemInterface,
} from "../../../common/types/interfaces";
import { removeClusterItems } from "../../../store/slices/cartSlice";

import { useCartValidation } from "./useCartValidation";

interface CdekOrderData {
  tariffCode: number;
  pickupPointCode?: string;
  pickupPointName?: string;
  pickupPointAddress?: string;
  pickupPointPhone?: string;
  tariffName?: string;
  periodMin?: number;
  periodMax?: number;
  deliveryDateMin?: string;
  deliveryDateMax?: string;
}

interface Props {
  cluster: any;
  cartItems: CartItem[];
  saleItemList: SaleItemInterface[];
  itemsTotal: number;
  deliveryFee: number;
  discountAmount: number;
  totalWithDelivery: number;
  deliveryType: DeliveryType;
  pickUpStore?: number;
  deliveryStoreId?: number | null;
  selectedAddressId?: number | null;
  paymentMethod: PaymentMethod;
  selectedPromo: PromoInterface | null;
  discountCode: string;
  bonusesToSpend?: number;
  desiredTime?: string | null;
  phone: string;
  comment?: string;
  cutleryCount?: number;
  selectedGiftItemId?: number;
  deliveryService?: DeliveryService;
  cdekOrder?: CdekOrderData;
}

export const useOrderCreation = ({
  cluster,
  cartItems,
  saleItemList,
  itemsTotal,
  deliveryFee,
  discountAmount,
  totalWithDelivery,
  deliveryType,
  pickUpStore,
  deliveryStoreId,
  selectedAddressId,
  paymentMethod,
  selectedPromo,
  discountCode,
  bonusesToSpend = 0,
  desiredTime,
  phone,
  comment,
  cutleryCount,
  selectedGiftItemId,
  deliveryService,
  cdekOrder,
}: Props) => {
  const api = getApi();
  const dispatch = useDispatch();
  const { roundMoney, isPromoApplicable } = useCartValidation();

  const createOrder = useCallback(async () => {
    if (selectedPromo) {
      const storeId =
        deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined;
      const isValid = isPromoApplicable(
        selectedPromo,
        cartItems,
        storeId,
        discountCode,
        deliveryType,
      );
      if (!isValid) {
        throw new Error(
          "Выбранная промо-акция не может быть применена к этому заказу",
        );
      }
    }

    let storeId: number | undefined;
    if (deliveryType === DeliveryType.PICKUP) {
      storeId = pickUpStore;
    } else if (deliveryType === DeliveryType.DELIVERY && deliveryStoreId) {
      storeId = deliveryStoreId;
    }

    const finalComment =
      cutleryCount && cutleryCount > 0
        ? [comment, `Приборы: ${cutleryCount}`].filter(Boolean).join("\n")
        : comment || "";

    const newOrder: OrderInterface = {
      status: OrderStatus.NEW,
      deliveryType,
      fullAddressId: selectedAddressId || undefined,
      paymentMethod,
      paymentStatus: PaymentStatus.PENDING,
      isPaid: false,
      items: cartItems.map((item) => {
        const saleItem = saleItemList.find(
          (el) => el.id === Number(item.saleItemId),
        );
        return {
          itemId: Number(item.saleItemId),
          name: saleItem?.name || item.name || `Товар #${item.saleItemId}`,
          price: item.price,
          quantity: item.quantity,
          total: roundMoney(item.price * item.quantity),
          unit: item.unit,
        };
      }),
      subtotal: itemsTotal,
      deliveryFee,
      total: totalWithDelivery,
      storeId,
      desiredTime: desiredTime || undefined,
      orderNumber: "",
      promoId: selectedPromo?.id,
      discountCode: discountCode || undefined,
      discountAmount,
      bonusesSpent: bonusesToSpend,
      phone_number: phone,
      description: finalComment,
      deliveryService,
      cdekOrder,
      clusterId: cluster?.id,
      selectedGiftItemId,
    } as OrderInterface;

    const result = await api.createOrder(cluster, newOrder);

    if (cluster?.id) {
      dispatch(removeClusterItems(cluster.id));
    }

    return result;
  }, [
    cluster,
    cartItems,
    saleItemList,
    itemsTotal,
    deliveryFee,
    discountAmount,
    totalWithDelivery,
    deliveryType,
    pickUpStore,
    deliveryStoreId,
    selectedAddressId,
    paymentMethod,
    selectedPromo,
    discountCode,
    bonusesToSpend,
    desiredTime,
    phone,
    comment,
    cutleryCount,
    selectedGiftItemId,
    deliveryService,
    cdekOrder,
    api,
    dispatch,
    roundMoney,
    isPromoApplicable,
  ]);

  return { createOrder };
};
